import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StonLogo from "@/components/StonLogo";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, Buildings, IdentificationBadge, Phone, SignOut, PaperPlaneTilt } from "@phosphor-icons/react";

const PROFESSIONS = ["Procurement", "Architect", "Contractor", "Project Manager"];

export default function CompleteProfile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [joiningCode, setJoiningCode] = useState("");
  const [profession, setProfession] = useState(user?.profession || "");
  const [contact, setContact] = useState(user?.contact_number || "");
  const [busy, setBusy] = useState(false);

  const [applyMode, setApplyMode] = useState(false);
  const [applyName, setApplyName] = useState("");
  const [applyEmail, setApplyEmail] = useState(user?.email || "");
  const [applyNotes, setApplyNotes] = useState("");
  const [applyBusy, setApplyBusy] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (user?.profile_complete) navigate("/home", { replace: true });
    if (user?.pending_company_application_id) setApplied(true);
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!joiningCode.trim()) return toast.error("Enter your joining code");
    if (!profession) return toast.error("Pick your profession");
    if (!contact.trim()) return toast.error("Enter your contact number");
    setBusy(true);
    try {
      await api.post("/profile/complete", {
        joining_code: joiningCode.trim().toUpperCase(),
        profession,
        contact_number: contact.trim(),
      });
      await refresh();
      toast.success("Profile complete — welcome to Ston Link");
      navigate("/home", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not link your account");
    }
    setBusy(false);
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!applyName.trim()) return toast.error("Enter your company name");
    if (!profession) return toast.error("Pick your profession");
    if (!contact.trim()) return toast.error("Enter your contact number");
    if (!applyEmail.trim()) return toast.error("Enter a contact email");
    setApplyBusy(true);
    try {
      await api.post("/company-applications", {
        proposed_company_name: applyName.trim(),
        profession,
        contact_number: contact.trim(),
        contact_email: applyEmail.trim(),
        notes: applyNotes.trim(),
      });
      toast.success("Application sent — the Ston Link team will reach out shortly");
      setApplied(true);
      setApplyMode(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to submit application");
    }
    setApplyBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F2EA] flex flex-col">
      {/* header */}
      <header className="border-b border-zinc-200 bg-white/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <StonLogo size={20} subtitle="Building Materials" className="text-zinc-900" />
          </div>
          <button
            onClick={logout}
            data-testid="complete-profile-logout"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10">
        <div className="mt-tiny text-[#FF5A1F]">One more step</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-zinc-900 mt-2">
          Complete your profile
        </h1>
        <p className="mt-3 text-zinc-600 max-w-xl">
          Hi {user?.name || "there"} — finish setting up your account before you can view projects and orders. Your details help us route quotations to the right team.
        </p>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          <div className="bg-white border border-zinc-200 p-5">
            <IdentificationBadge size={22} className="text-[#0047AB]" weight="duotone" />
            <div className="mt-tiny text-zinc-500 mt-2">Joining code</div>
            <div className="text-sm font-medium text-zinc-900 mt-1">Link to your team dashboard using the code your Ston Link contact shared with you.</div>
          </div>
          <div className="bg-white border border-zinc-200 p-5">
            <Buildings size={22} className="text-[#0047AB]" weight="duotone" />
            <div className="mt-tiny text-zinc-500 mt-2">Profession</div>
            <div className="text-sm font-medium text-zinc-900 mt-1">Tell us what you do — Procurement, Architect, Contractor or PM.</div>
          </div>
          <div className="bg-white border border-zinc-200 p-5">
            <Phone size={22} className="text-[#0047AB]" weight="duotone" />
            <div className="mt-tiny text-zinc-500 mt-2">Contact number</div>
            <div className="text-sm font-medium text-zinc-900 mt-1">The business phone we'll use for urgent order updates.</div>
          </div>
        </div>

        {/* Main form */}
        {!applyMode && (
          <form
            onSubmit={submit}
            className="mt-8 bg-white border border-zinc-200 p-6 md:p-8 space-y-5"
            data-testid="complete-profile-form"
          >
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Company joining code *</div>
              <Input
                data-testid="joining-code-input"
                value={joiningCode}
                onChange={(e) => setJoiningCode(e.target.value.toUpperCase())}
                placeholder="e.g. STON-A1B2C3"
                className="h-12 rounded-none border-zinc-200 font-mono-mt uppercase tracking-widest"
              />
              <div className="mt-tiny text-zinc-400 mt-1">Ask your Ston Link contact if you don't have one.</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Profession *</div>
                <Select value={profession} onValueChange={setProfession}>
                  <SelectTrigger data-testid="profession-select" className="h-12 rounded-none border-zinc-200">
                    <SelectValue placeholder="Pick your role" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {PROFESSIONS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Contact number *</div>
                <Input
                  data-testid="contact-number-input"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="+1 555 000 4321"
                  className="h-12 rounded-none border-zinc-200"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="complete-profile-submit"
              className="w-full bg-[#0047AB] hover:bg-[#003380] text-white h-12 font-medium inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              <CheckCircle size={18} weight="fill" />
              {busy ? "Linking..." : "Link my account"}
            </button>

            <div className="pt-4 border-t border-zinc-200">
              {applied ? (
                <div className="text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" /> Application sent — we'll reach out to activate your workspace.
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="mt-tiny text-zinc-500">No joining code?</div>
                    <div className="text-sm font-medium text-zinc-900">Apply to create a new company account for your team.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setApplyMode(true)}
                    data-testid="apply-new-company-button"
                    className="inline-flex items-center gap-2 border border-zinc-300 hover:border-[#FF5A1F] hover:text-[#FF5A1F] px-4 py-2.5 text-sm font-medium transition-colors"
                  >
                    Apply to create a new company account
                  </button>
                </div>
              )}
            </div>
          </form>
        )}

        {applyMode && (
          <form
            onSubmit={submitApplication}
            className="mt-8 bg-white border border-[#FF5A1F] p-6 md:p-8 space-y-5"
            data-testid="apply-new-company-form"
          >
            <div className="mt-tiny text-[#FF5A1F]">Company application</div>
            <h2 className="font-display text-2xl font-black text-zinc-900">Tell us about your company</h2>
            <p className="text-sm text-zinc-600">
              We'll set you up with a new Ston Link workspace and joining code once we've verified your details.
            </p>

            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Company name *</div>
              <Input
                data-testid="apply-company-name-input"
                value={applyName}
                onChange={(e) => setApplyName(e.target.value)}
                placeholder="BuildCorp Interiors LLC"
                className="h-12 rounded-none border-zinc-200"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Profession *</div>
                <Select value={profession} onValueChange={setProfession}>
                  <SelectTrigger data-testid="apply-profession-select" className="h-12 rounded-none border-zinc-200">
                    <SelectValue placeholder="Pick your role" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {PROFESSIONS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Contact number *</div>
                <Input
                  data-testid="apply-contact-number-input"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="+1 555 000 4321"
                  className="h-12 rounded-none border-zinc-200"
                />
              </div>
            </div>

            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Contact email *</div>
              <Input
                data-testid="apply-contact-email-input"
                type="email"
                value={applyEmail}
                onChange={(e) => setApplyEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-12 rounded-none border-zinc-200"
              />
            </div>

            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Anything else we should know?</div>
              <Textarea
                data-testid="apply-notes-input"
                value={applyNotes}
                onChange={(e) => setApplyNotes(e.target.value)}
                placeholder="Project scale, materials, timeline..."
                className="rounded-none border-zinc-200 min-h-[100px]"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={applyBusy}
                data-testid="apply-submit-button"
                className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-3 font-medium transition-colors disabled:opacity-60"
              >
                <PaperPlaneTilt size={18} weight="fill" />
                {applyBusy ? "Sending..." : "Send application"}
              </button>
              <button
                type="button"
                onClick={() => setApplyMode(false)}
                className="border border-zinc-300 hover:border-zinc-900 px-6 py-3 text-sm"
              >
                Back to joining code
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
